/**
 * LAB-109: product images in Copilot answers. Copilot Studio replies carry no images, so when
 * the user asks to see a product, LabWizard looks it up in the brand-website pages it has
 * crawled (scoped to the user's brands) and adds the result under the answer as markdown.
 * Keeping it in the text means it is saved with the chat and shows again when reopened.
 */

import { markdownUrl } from "./copilotTables"

export type CopilotProductImage = {
  url: string
  alt: string
  pageTitle: string
  pageUrl: string
  website: string
  match: "exact" | "related"
}

export type CopilotProductImageResult = {
  product: string
  exactMatch: boolean
  images: CopilotProductImage[]
}

export const PRODUCT_IMAGES_HEADING =
  "**Product images from LabWizard sources**"

const IMAGE_REQUEST =
  /\b(?:images?|photos?|pictures?|pics?|photographs?|visuals?)\b|\blooks?\s+like\b/i

/** A question asking to see a product. Uploaded files have their own image flow (LAB-99). */
export function isProductImageRequest(text: string) {
  return IMAGE_REQUEST.test(text || "")
}

export const PRODUCT_IMAGE_GROUNDING = [
  "Image request: LabWizard shows product images from brand websites and SharePoint image files under your answer, each with its source link.",
  "Do not describe what a product image shows, and do not link an image, unless a source you cite contains it.",
  "If your sources have no image of this exact product, say so plainly. Never present an image of a similar product as this product.",
]

/** Brackets would break the markdown, and pipes would read as a table row (LAB-106). */
function label(value: string) {
  return (value || "")
    .replace(/[[\]]/g, "")
    .replace(/\s*\|\s*/g, " - ")
    .replace(/\s+/g, " ")
    .trim()
}

function sourceLink(image: CopilotProductImage) {
  return `[${label(image.pageTitle) || "Source page"}](${markdownUrl(
    image.pageUrl
  )})`
}

function websiteName(images: CopilotProductImage[]) {
  const names = [
    ...new Set(images.map((image) => image.website).filter(Boolean)),
  ]
  if (names.length === 1) {
    return names[0].toLowerCase() === "sharepoint"
      ? "SharePoint"
      : `the ${names[0]} website`
  }
  if (names.some((name) => name.toLowerCase() === "sharepoint")) {
    return "LabWizard sources (brand websites and SharePoint)"
  }
  return "the brand websites"
}

/** The block added under the answer. Each image line carries its source page as the caption. */
export function productImagesMarkdown(result: CopilotProductImageResult) {
  const product = label(result.product) || "this product"
  const images = result.images.filter(
    (image) =>
      /^https:\/\//i.test(image.url) ||
      /copilot-sharepoint-image/i.test(image.url)
  )
  if (!images.length) {
    return [
      PRODUCT_IMAGES_HEADING,
      "",
      `No image of "${product}" was found in LabWizard's brand websites or SharePoint image libraries for your brands. Check the brand website, SharePoint, or ask an expert before sharing one with a customer.`,
    ].join("\n")
  }
  const notice = result.exactMatch
    ? `Images of "${product}" from ${websiteName(
        images
      )}. Check the source page before sharing them with a customer.`
    : `An exact image of "${product}" was not found in LabWizard's sources. These related images are from ${websiteName(
        images
      )} and may not show the exact product.`
  return [
    PRODUCT_IMAGES_HEADING,
    "",
    notice,
    "",
    ...images.map((image) => {
      const caption =
        image.match === "exact"
          ? `Source: ${sourceLink(image)}`
          : `Related image, not the exact product. Source: ${sourceLink(image)}`
      return `![${label(image.alt) || "Product image"}](${markdownUrl(
        image.url
      )}) ${caption}`
    }),
  ].join("\n")
}

/** Separates the image block so source checks only review the agent's own answer. */
export function splitProductImages(text: string) {
  const start = (text || "").indexOf(PRODUCT_IMAGES_HEADING)
  if (start < 0) return { answer: text || "", images: "" }
  return { answer: text.slice(0, start).trimEnd(), images: text.slice(start) }
}

export function withProductImages(
  answer: string,
  result: CopilotProductImageResult
) {
  return [splitProductImages(answer).answer, productImagesMarkdown(result)]
    .filter(Boolean)
    .join("\n\n")
}
